import { darkGrayFontColor, nameCircles } from '../../web/styles/colors';
import { createBodyVarApplier } from '../../web/styles/customPageTheme';

const BRAND_FILL = 'var(--custom-primary-button-bg)';

// An unsupported color-mix() is invalid at substitution time, which paints nothing rather than
// falling back to the var's default, so the neutral tint stands in on older browsers.
const supportsColorMix =
	typeof CSS !== 'undefined' &&
	typeof CSS.supports === 'function' &&
	CSS.supports('background', 'color-mix(in srgb, red 15%, white)');

// Replacements for the scheduler's blue accents, applied only when the account has custom
// primary-button colors so the blues don't clash with the customer's brand. Consumed by
// MeetingContainer (live page, set on document.body so the root-level error modal inherits them)
// and by the admin MeetingPagePreview (set inline on the preview canvas). Scheduler styles read
// each var with the default blue as its fallback, so pages without customization are untouched.
//
// The accent set is neutral gray and covers static chrome (header badge, date pills, dividers).
// The interactive set follows the customer's button color instead: gray on the day circles and
// time pills reads as disabled, and those are the page's primary controls.
export const SCHEDULER_ACCENT_VARS: Record<string, string> = {
	'--scheduler-accent-border': 'var(--palette-ace-gray)',
	'--scheduler-accent-fill': darkGrayFontColor,
	'--scheduler-accent-link': 'var(--palette-top-hat-black)',
	'--scheduler-accent-link-hover': darkGrayFontColor,
	'--scheduler-accent-tint': 'var(--palette-shuffle-gray)',
	'--scheduler-interactive-fill': BRAND_FILL,
	'--scheduler-interactive-text': 'var(--custom-primary-button-text, #fff)',
	// Tinted into the neutral gray rather than white so a near-white brand color still leaves a
	// visible surface. The result stays light at any brand color, so these keep the page's dark
	// text rather than the button's text color.
	'--scheduler-interactive-tint': supportsColorMix
		? `color-mix(in srgb, ${BRAND_FILL} 14%, var(--palette-shuffle-gray))`
		: 'var(--palette-shuffle-gray)',
	// Sits between the resting tint and the solid selected fill.
	'--scheduler-interactive-tint-hover': supportsColorMix
		? `color-mix(in srgb, ${BRAND_FILL} 30%, var(--palette-shuffle-gray))`
		: nameCircles,
};

/** Applies the accent vars to <body> and returns the matching cleanup. */
export const applySchedulerAccentVars = createBodyVarApplier(SCHEDULER_ACCENT_VARS);
